<script setup>
import SectionHero from '@/components/SectionHero.vue';
import SectionHalfText from '@/components/SectionHalfText.vue';
import FlatCard from '@/components/FlatCard.vue';

import {useRouting} from "@/composables/routing.js";
const {href,url} = useRouting();

import { Head } from '@inertiajs/vue3'
import { Link } from '@inertiajs/vue3';
</script>

<template>
    <Head>
        <title>Funktionen unserer managed Cloud</title>
        <meta name="description" content="Entdecken Sie die Funktionen unserer managed Cloud. Spaces, Datenbanken, Routes und Domains">
        <link rel="canonical" :href="url('/features', 'de')">
        <link rel="alternate" hreflang="en" :href="url('/features', 'en')">
        <link rel="alternate" hreflang="de" :href="url('/features', 'de')">
    </Head>
    <div>
        <section-hero title="Funktionen unserer managed Cloud"
                      subtitle="Unsere managed Cloud bietet Ihnen die perfekte Kombination aus Performance, Sicherheit
                      und Flexibilität. Egal ob Website, Onlineshop, Agenturprojekte oder SaaS-Anwendungen –
                      wir haben die passende Lösung für Sie."
        >
        </section-hero>

        <section-half-text class="mt-10"
                           id="spaces"
                           title="Spaces"
        >
            <template #default>
                <p class="text-lg mt-10">
                    Spaces sind moderne, containerbasierte Webspaces. Sie bieten die Vorteile der Cloud in Bezug
                    auf Skalierung und garantiert zugewiesene Performance, sind dabei aber so einfach zu konfigurieren
                    wie ein klassischer Webspace.
                </p>
                <p class="text-lg mt-2">
                    Die Container-Technologie sorgt für eine Isolierung der Ressourcen sodass Sie die gebuchte Leistung
                    jederzeit exklusiv zur Verfügung haben. Gleichzeitig lässt sich der Container jederzeit auf einem
                    anderen Cluster-Knoten starten, sollte der aktuelle Knoten ausfallen. Dies geschieht automatisch.
                </p>
                <p class="text-lg mt-2">
                    Der SFTP und SSH Zugang ermöglicht ein einfaches Hochladen der Anwendung und bietet darüber
                    hinaus die Möglichkeit, zur Laufzeit auf die Anwendung zuzugreifen. Der Space ist ideal für
                    kleine bis mittlere Webprojekte, die eine einfache und flexible Hosting-Lösung benötigen.
                </p>
            </template>
            <template #extra>
                <div class="col-span-2 flex items-center">
                    <img src="/assets/space-ssh.png"
                         alt="Spaces"
                         class="w-full xl:mx-10 mx-auto rounded-2xl shadow-2xl max-w-full h-auto" />
                </div>
            </template>
        </section-half-text>

        <section-half-text class="mt-32 lg:mt-56"
                           id="databases"
                           text-right
                           title="Datenbanken"
        >
            <template #default>
                <p class="text-lg mt-10">
                    Moderne Webanwendungen benötigen leistungsfähige Datenbanken. Die Datenbankanfragen haben einen
                    entscheidenden Einfluss auf die Performance Ihrer Anwendung.
                </p>
                <p class="text-lg mt-2">
                    In unserer managed Cloud können Sie mit wenigen Klicks eine neue Datenbank anlegen. Im Hintergrund
                    wird hierbei ein dedizierter Datenbankserver für Sie bereitgestellt. Die fest zugewiesene Leistung
                    können Sie jederzeit skalieren.
                </p>
                <p class="text-lg mt-2">
                    Wir berechnen keine zusätzlichen Gebühren. Sie zahlen nur die gebuchte Performance sowie den
                    Speicherplatz.
                </p>
            </template>
            <template #extra>
                <div class="col-span-2 flex items-center">
                    <img src="/assets/create-database.png"
                         alt="Databases"
                         class="w-full xl:mx-10 mx-auto rounded-2xl shadow-2xl" />
                </div>
            </template>
        </section-half-text>

        <section-half-text class="mt-32 lg:mt-56"
                           id="routes"
                           title="Routes"
        >
            <template #default>
                <p class="text-lg mt-10">
                    Mit Routes können Sie Ihre Anwendung über verschiedene Domains und Subdomains erreichbar machen.
                    Zudem können Sie ein pfadbasiertes Routing konfigurieren. Es lassen sich sowohl Ziele als auch
                    Weiterleitungen definieren.
                </p>
                <p class="text-lg mt-2">
                    Alle Routes werden auf unserer redundanten Loadbalancer Infrastruktur aufgelöst. Dies sorgt für
                    eine hohe Verfügbarkeit und Performance Ihrer Anwendung. Die Konfiguration ist für Sie dennoch
                    einfach und mit wenigen Klicks erledigt.
                </p>
                <p class="text-lg mt-2">
                    Für Routes fallen keine Gebühren an. Ein kostenloses SSL Zertifikat ist inklusive.
                </p>
            </template>
            <template #extra>
                <div class="col-span-2 flex items-center">
                    <img src="/assets/create-route.png"
                         alt="Routes"
                         class="w-full xl:mx-10 mx-auto rounded-2xl shadow-2xl" />
                </div>
            </template>
        </section-half-text>

        <section-half-text class="mt-32 lg:mt-56"
                           id="domains"
                           text-right
                           title="Domains"
        >
            <template #default>
                <p class="text-lg mt-10">
                    Unser Domain-Robot ermöglicht Ihnen die einfache und schnelle Registrierung Ihrer Wunschdomains.
                    Registrierung und Transfer werden in Echtzeit ausgeführt. Die zur Domain gehörenden Kontakte
                    können Sie zentral verwalten und wiederverwenden.
                </p>
                <p class="text-lg mt-2">
                    Wir bieten Ihnen eine große Auswahl an Domainendungen und attraktive Preise schon ab der 1. Domain.
                    Damit ist das Angebot insbesondere auch für Agenturen und Reseller interessant. Optional besteht die
                    Möglichkeit der Nutzung unserer REST-API zur Automatisierung von Domainprozessen.
                </p>
                <p class="text-lg mt-2">
                    Sie können eigene Nameserver definieren oder eine von uns verwaltete globale Anycast DNS Zone nutzen.
                </p>
                <Link :href="href('/pricing#domains')"
                      class="block text-lg underline font-bold mt-4"
                >
                    Zur detaillierten Preistabelle
                </Link>
            </template>
            <template #extra>
                <flat-card title=".DE ab 4,19 €"
                           text="Registrieren Sie Ihre .de Domain für nur 4,19€ (Neuregistrierung +0,80€)"
                />
                <flat-card title=".EU nur 8,86 €"
                           text="Registrieren Sie Ihre .eu Domain für nur 8,86€"
                />
                <flat-card title=".AT nur 12,86 €"
                           text="Registrieren Sie Ihre .de Domain für nur 12,86€"
                />
                <flat-card title=".COM nur 14,55 €"
                           text="Registrieren Sie Ihre .com Domain für nur 14,55€"
                />
                <flat-card title=".NET nur 16,34 €"
                           text="Registrieren Sie Ihre .net Domain für nur 16,34€"
                />
                <flat-card title=".ORG nur 14,01 €"
                           text="Registrieren Sie Ihre .org Domain für nur 14,01€"
                />
            </template>
        </section-half-text>
    </div>

</template>
